import { DateTime, FixedOffsetZone } from "luxon";

import { InvalidValue } from "../InvalidValue";

/**
 * The base class for all date serializers.
 */
export abstract class DateSerializer {
  /**
   * Gets the ISO date serializer.
   */
  static get Iso(): DateSerializer {
    return IsoDateSerializer.instance;
  }

  /**
   * Gets the ISO date serializer with UTC zone (no offset).
   */
  static get IsoUtc(): DateSerializer {
    return IsoUtcDateSerializer.instance;
  }

  /**
   * Gets the Literal date serializer.
   */
  static get Literal(): DateSerializer {
    return LiteralDateSerializer.instance;
  }

  /**
   * Indicates whether the specified value is valid for conversion.
   * @param value The value to validate.
   * @returns True if valid, otherwise false.
   */
  abstract valid(value: string | InvalidValue | null): boolean;

  /**
   * Deserializes a Date from the specified string value.
   * @param value The string value to convert the date from.
   * @param zone The zone to place the date within.
   * @returns The Date.
   */
  abstract from(value: string | InvalidValue | null, zone: "utc" | "local"): Date | InvalidValue | null;

  /**
   * Serializes a Date to the specified string value.
   * @param value The Date value to convert the date string from.
   * @param zone The zone to use for formatting the date. Defaults to `"utc"`.
   * @returns The date string.
   */
  abstract to(value: Date | InvalidValue | null, zone?: "utc" | "local"): string | InvalidValue | null;

  /**
   * Initializes a new instance of the DateSerializer class.
   */
  protected constructor() {
    // do nothing.
  }
}

class IsoDateSerializer extends DateSerializer {
  static instance: DateSerializer = new IsoDateSerializer();

  valid(value: string | InvalidValue | null): boolean {
    if (value !== null && typeof value === "string") {
      const date = DateTime.fromISO(value);
      if (date.isValid) return true;
    }

    return false;
  }

  from(value: string | InvalidValue | null, zone: "utc" | "local"): Date | InvalidValue | null {
    if (value === null) return null;

    if (typeof value === "string") {
      const date = DateTime.fromISO(value, {
        setZone: true,
        zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local",
      });

      return !date.isValid ? { type: "invalid", value: "" } : date.toJSDate();
    }

    return value;
  }

  to(value: Date | InvalidValue | null, zone: "utc" | "local" = "utc"): string | InvalidValue | null {
    if (value === null) return null;

    if (value instanceof Date) {
      const dateTime = DateTime.fromJSDate(value, { zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local" });
      return dateTime.toISO({ includeOffset: true });
    }

    return value;
  }
}

class IsoUtcDateSerializer extends DateSerializer {

  static instance: DateSerializer = new IsoUtcDateSerializer();

  valid(value: string | InvalidValue | null): boolean {
    if (value !== null && typeof value === "string") {
      const date = DateTime.fromISO(value);
      if (date.isValid) return true;
    }

    return false;
  }

  from(value: string | InvalidValue | null, zone: "utc" | "local"): Date | InvalidValue | null {
    if (value === null) return null;

    if (typeof value === "string") {
      const date = DateTime.fromISO(value, {
        setZone: true,
        zone: (zone === "utc") ? FixedOffsetZone.utcInstance : "local",
      });

      return (!date.isValid) ? { type: "invalid", value: "" } : date.toJSDate();
    }

    return value;
  }

  to(value: Date | InvalidValue | null): string | InvalidValue | null {
    if (value === null) return null;

    if (value instanceof Date) {
      const dateTime = DateTime.fromJSDate(value, { zone: 'utc' });
      return dateTime.toISO();
    }

    return value;
  }
}

class LiteralDateSerializer extends DateSerializer {
  static instance: DateSerializer = new LiteralDateSerializer();

  valid(value: string | InvalidValue | null): boolean {
    if (value !== null && typeof value === "string") {
      const date = DateTime.fromFormat(value, "MM/dd/yyyy");
      if (date.isValid) return true;
    }

    return false;
  }

  from(value: string | InvalidValue | null, zone: "utc" | "local"): Date | InvalidValue | null {
    if (value === null) return null;

    if (typeof value === "string") {
      const date = DateTime.fromFormat(value, "MM/dd/yyyy", {
        setZone: true,
        zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local",
      });

      return !date.isValid ? { type: "invalid", value: "" } : date.toJSDate();
    }

    return value;
  }

  to(value: Date | InvalidValue | null, zone: "utc" | "local" = "utc"): string | InvalidValue | null {
    if (value === null) return null;

    if (value instanceof Date) {
      const dateTime = DateTime.fromJSDate(value, { zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local" });
      return dateTime.toFormat("MM/dd/yyyy");
    }

    return value;
  }
}
